import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 2rem;
  background: linear-gradient(135deg, #170303 0%, #2b0909 55%, #400707 100%);
`;

const FormContainer = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ee6d6d 0%, #e94444 45%, #b31515 100%);
  }
`;

const FormTitle = styled(motion.h1)`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 0.875rem;
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(228, 27, 27, 0.28);
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(228, 27, 27, 0.38);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SSOContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const GoogleButton = styled(Button)`
  background: linear-gradient(135deg, #db4437 0%, #b7342b 100%);
`;

const MicrosoftButton = styled(Button)`
  background: linear-gradient(135deg, #ee6d6d 0%, #e41b1b 100%);
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  width: 100%;
  line-height: 1;
`;

const IconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 50%;
  background-color: rgba(252, 232, 232, 0.14);
  flex-shrink: 0;
`;

const ButtonText = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
`;

const GoogleIcon: React.FC = () => {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M21.8 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.5c-.2 1.4-1 2.6-2.1 3.4v2.8h3.4c2-1.8 3-4.5 3-8.2z"
      />
      <path
        fill="#FF3D00"
        d="M12 22c2.7 0 5-.9 6.7-2.5l-3.4-2.8c-.9.6-2.1 1-3.3 1-2.5 0-4.7-1.7-5.5-4H3v2.9C4.7 20.1 8.1 22 12 22z"
      />
      <path
        fill="#4CAF50"
        d="M6.5 13.7c-.2-.6-.3-1.1-.3-1.7s.1-1.2.3-1.7V7.4H3C2.4 8.7 2 10.3 2 12s.4 3.3 1 4.6l3.5-2.9z"
      />
      <path
        fill="#1976D2"
        d="M12 6.3c1.5 0 2.9.5 3.9 1.5l2.9-2.9C17 3.2 14.8 2 12 2 8.1 2 4.7 3.9 3 7.4l3.5 2.9c.8-2.3 3-4 5.5-4z"
      />
    </svg>
  );
};

const MicrosoftIcon: React.FC = () => {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" fill="#F35325" />
      <rect x="13" y="3" width="8" height="8" fill="#81BC06" />
      <rect x="3" y="13" width="8" height="8" fill="#05A6F0" />
      <rect x="13" y="13" width="8" height="8" fill="#FFBA08" />
    </svg>
  );
};

const ErrorMessage = styled(motion.div)`
  color: #e94560;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
  padding: 0.75rem;
  background-color: #ffeaea;
  border-radius: 8px;
  border-left: 4px solid #e94560;
`;

const formVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.02,
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

const errorVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithMicrosoft, error, loading, clearError } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      import("../utils/secureStorage").then(({ getSecureStorage }) => {
        const secureData = getSecureStorage();
        const token = secureData.token;
        if (token) {
          navigate("/");
        }
      });
    } catch (e) {
      // No-op
    }
  }, [navigate]);

  const handleGoogleLogin = async () => {
    if (error) clearError();
    const success = await loginWithGoogle();
    if (success) {
      navigate("/");
    }
  };

  const handleMicrosoftLogin = async () => {
    if (error) clearError();
    const success = await loginWithMicrosoft();
    if (success) {
      navigate("/");
    }
  };

  return (
    <PageContainer>
      <FormContainer initial="hidden" animate="visible" variants={formVariants}>
        <FormTitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {t("auth.login")}
        </FormTitle>

        <FormSubtitle>{t("auth.loginSubtitle")}</FormSubtitle>

        <SSOContainer>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <GoogleButton
              type="button"
              whileHover="hover"
              whileTap="tap"
              variants={buttonVariants}
              disabled={loading}
              onClick={handleGoogleLogin}
            >
              <ButtonContent>
                <IconBadge>
                  <GoogleIcon />
                </IconBadge>
                <ButtonText>{loading ? t("common.loading") : t("auth.sso.google")}</ButtonText>
              </ButtonContent>
            </GoogleButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <MicrosoftButton
              type="button"
              whileHover="hover"
              whileTap="tap"
              variants={buttonVariants}
              disabled={loading}
              onClick={handleMicrosoftLogin}
            >
              <ButtonContent>
                <IconBadge>
                  <MicrosoftIcon />
                </IconBadge>
                <ButtonText>{loading ? t("common.loading") : t("auth.sso.microsoft")}</ButtonText>
              </ButtonContent>
            </MicrosoftButton>
          </motion.div>

          {error && (
            <ErrorMessage
              initial="hidden"
              animate="visible"
              variants={errorVariants}
            >
              {error}
            </ErrorMessage>
          )}
        </SSOContainer>
      </FormContainer>
    </PageContainer>
  );
};

export default LoginPage;
